import React, {useMemo} from "react";
import {Container, Table} from "react-bootstrap";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {formatDuration, formatTimeDE} from "../../Components/Nutzung/format.js";
import {MitarbeiterLink} from "../../Components/Nutzung/Links.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";

const WOCHENTAG_KURZ = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/**
 * "Tag" kommt als reiner Kalendertag (YYYY-MM-DD, ohne Uhrzeit). Bewusst
 * OHNE new Date(iso)/Zeitzonen-Umrechnung formatiert (siehe globale Regel
 * "Uhrzeiten - immer die Zeitzone prüfen"): ein nackter Tagesstring wird
 * beim Parsen als UTC-Mitternacht gedeutet und könnte in Europe/Berlin auf
 * den Vor- oder Folgetag rutschen. Die Kalenderteile werden deshalb direkt
 * aus dem String gelesen, wie schon in useNutzungFilter (addDaysISO).
 */
function formatTagKurz(tagISO) {
    if (!tagISO) {
        return "–";
    }
    const [jahr, monat, tag] = String(tagISO).split("-").map(Number);
    if (!jahr || !monat || !tag) {
        return tagISO;
    }
    const wochentag = WOCHENTAG_KURZ[new Date(jahr, monat - 1, tag).getDay()];
    return `${wochentag}, ${String(tag).padStart(2, "0")}.${String(monat).padStart(2, "0")}.`;
}

/** Uhrzeit-Anteil eines ISO-Zeitstempels in Europe/Berlin als Bruchteil des
 * Tages (0 = Mitternacht, 1 = 24 Uhr) - Grundlage für die Position im Band. */
const bandZeitFormatter = new Intl.DateTimeFormat("de-DE", {timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit", hourCycle: "h23"});
function tagesanteil(iso) {
    if (!iso) {
        return null;
    }
    const datum = new Date(iso);
    if (Number.isNaN(datum.getTime())) {
        return null;
    }
    const teile = bandZeitFormatter.formatToParts(datum);
    const stunde = Number(teile.find((teil) => teil.type === "hour")?.value ?? 0);
    const minute = Number(teile.find((teil) => teil.type === "minute")?.value ?? 0);
    return (stunde * 60 + minute) / 1440;
}

/**
 * Kleines Band je Zeile, das den Tagesverlauf ANDEUTET (Auftrag Punkt 5) -
 * die Spanne von der ersten Anmeldung bis zur letzten Handlung als
 * Balkenabschnitt über einen 24-Stunden-Maßstab, dessen Farbsättigung mit
 * dem Anteil aktiver Zeit an der Anwesenheit steigt. Es sind keine
 * Einzelabschnitte bekannt (anders als das Tagesband im Profil, das einen
 * eigenen Endpunkt hat) - deshalb bewusst nur eine Andeutung, keine exakte
 * Zeitleiste.
 */
function TagesMiniband({tag}) {
    const von = tagesanteil(tag.erste_anmeldung);
    const bis = tagesanteil(tag.letzte_aktion);
    if (von === null || bis === null) {
        return <span className="text-muted small">–</span>;
    }
    const anwesend = Math.max(tag.anwesend_sekunden || 0, 1);
    const dichte = Math.min(1, Math.max(0, (tag.aktiv_sekunden || 0) / anwesend));
    const breite = Math.max((bis - von) * 100, 0.8);

    return (
        <div className="position-relative rounded-1" style={{height: 14, minWidth: 140, background: "#eef0f3", overflow: "hidden"}}>
            <div
                className="position-absolute top-0 h-100 rounded-1"
                style={{
                    left: `${Math.max(von * 100, 0)}%`,
                    width: `${breite}%`,
                    // Gleicher Blauton wie heatmapColor() in format.js, hier
                    // aber als Sättigung der Aktivitätsdichte statt eines
                    // Rangwerts - für ein durchgängiges Blau als "Nutzung"-Farbe.
                    background: `rgba(47, 111, 208, ${0.25 + dichte * 0.6})`,
                }}
            />
        </div>
    );
}

/**
 * Anwesenheitszeiten - Tabelle Mitarbeiter × Tage: erste Anmeldung, letzte
 * Handlung, Anwesenheit und aktive Zeit, dazu je Zeile das andeutende
 * Tagesband. Zeilen sind je Mitarbeiter gruppiert (Mitarbeiterzelle über
 * rowSpan), neuester Tag zuerst.
 */
export default function NutzungAnwesenheit() {
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const {data, loading, error} = useStatisticsData("/nutzung/daten/anwesenheit", filters);

    const gruppen = useMemo(() => {
        const rows = Array.isArray(data) ? data : [];
        return rows
            .map((person) => ({
                ...person,
                tage: Array.isArray(person.tage) ? [...person.tage].sort((a, b) => (a.tag < b.tag ? 1 : -1)) : [],
            }))
            .filter((person) => person.tage.length > 0);
    }, [data]);

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Mitarbeiter" titel="Anwesenheitszeiten" nutzungFilters={nutzungFilters}>
                <NutzungWidget
                    title="Anwesenheitszeiten"
                    help="Erste Anmeldung, letzte Handlung sowie Anwesenheit und aktive Zeit je Mitarbeiter und Tag im gewählten Zeitraum."
                    loading={loading}
                    error={error}
                    empty={!loading && !error && gruppen.length === 0}
                    height={420}
                >
                    <div style={{overflowX: "auto"}}>
                        <Table hover size="sm" className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Mitarbeiter</th>
                                    <th>Tag</th>
                                    <th>Erste Anmeldung</th>
                                    <th>Letzte Handlung</th>
                                    <th className="text-end">Anwesenheit</th>
                                    <th className="text-end d-inline-flex align-items-center gap-1">
                                        Aktive Zeit
                                        <HelpTooltip placement="top" size={0.75} id="anwesenheit-aktive-zeit" ariaLabel="Hinweis zu aktiver Zeit">
                                            Misst, wie dicht im Ordermanagement gearbeitet wird. Telefonate, Arbeit in Weclapp oder im
                                            Lager sind darin nicht enthalten - die Zahl ist kein Maß für Arbeitsleistung.
                                        </HelpTooltip>
                                    </th>
                                    <th>
                                        Tagesverlauf
                                        <HelpTooltip placement="top" size={0.75} id="anwesenheit-band" ariaLabel="Hinweis zum Tagesverlauf-Band">
                                            Blauer Abschnitt: Zeitspanne von der ersten Anmeldung bis zur letzten Handlung, über einen
                                            24-Stunden-Maßstab. Je kräftiger die Farbe, desto größer der Anteil aktiver Zeit daran.
                                        </HelpTooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {gruppen.map((person) =>
                                    person.tage.map((tag, index) => (
                                        <tr key={`${person.user_id}-${tag.tag}`} className={index === 0 ? "border-top" : undefined}>
                                            {index === 0 && (
                                                <td rowSpan={person.tage.length} className="align-top fw-semibold">
                                                    <MitarbeiterLink id={person.user_id} name={person.name} />
                                                </td>
                                            )}
                                            <td>{formatTagKurz(tag.tag)}</td>
                                            <td>{tag.erste_anmeldung ? `${formatTimeDE(tag.erste_anmeldung)} Uhr` : "–"}</td>
                                            <td>{tag.letzte_aktion ? `${formatTimeDE(tag.letzte_aktion)} Uhr` : "–"}</td>
                                            <td className="text-end">{formatDuration(tag.anwesend_sekunden)}</td>
                                            <td className="text-end">{formatDuration(tag.aktiv_sekunden)}</td>
                                            <td>
                                                <TagesMiniband tag={tag} />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </NutzungWidget>
            </NutzungLayout>
        </Container>
    );
}
